import { useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "@/components/feature/MainLayout";
import UserInfoHeader from "./components/UserInfoHeader";
import DevicePanel from "./components/DevicePanel";
import UsageChart from "./components/UsageChart";
import SafetyScore from "./components/SafetyScore";
import UserPortrait from "./components/UserPortrait";
import AIAnalysisPanel from "./components/AIAnalysisPanel";
import { residentUsers, commercialUsers } from "@/mocks/users";

const tabs = [
  { key: "overview", label: "档案总览", icon: "ri-file-user-line" },
  { key: "device", label: "设备信息", icon: "ri-cpu-line" },
  { key: "usage", label: "用能分析", icon: "ri-bar-chart-line" },
  { key: "safety", label: "安全评分", icon: "ri-shield-check-line" },
  { key: "ai", label: "AI分析", icon: "ri-brain-line" },
  { key: "portrait", label: "用户画像", icon: "ri-user-heart-line" },
];

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState("overview");

  const allUsers = [
    ...residentUsers.map(u => ({ ...u, category: "居民用户" })),
    ...commercialUsers.map(u => ({ ...u, category: "工商业用户" })),
  ];

  const user = allUsers.find(u => u.id === id);

  if (!user) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-unfollow-line text-gray-400 text-2xl"></i>
            </div>
            <p className="text-gray-500 text-sm">未找到用户信息</p>
            <p className="text-gray-400 text-xs mt-1">用户编号：{id}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const isResident = !("type" in user) || !(user as { type?: string }).type;
  const hasPortrait = isResident && "portrait" in user;

  const filteredTabs = isResident ? tabs : tabs.filter(t => t.key !== "portrait");

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Header */}
        <UserInfoHeader user={user as Parameters<typeof UserInfoHeader>[0]["user"]} />

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-100 px-4">
          <div className="flex items-center gap-1 overflow-x-auto">
            {filteredTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? "border-[#E8531A] text-[#E8531A]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-sm`}></i>
                </div>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <DevicePanel devices={user.devices as Parameters<typeof DevicePanel>[0]["devices"]} />
            </div>
            <div className="space-y-4">
              <SafetyScore
                safetyLevel={user.safetyLevel}
                safetyGrade={user.safetyGrade}
                lastInspection={user.lastInspection}
                nextInspection={user.nextInspection}
                insurance={user.insurance}
                devices={user.devices}
              />
            </div>
          </div>
        )}

        {activeTab === "device" && (
          <DevicePanel devices={user.devices as Parameters<typeof DevicePanel>[0]["devices"]} />
        )}

        {activeTab === "usage" && (
          <UsageChart usageHistory={user.usageHistory} userName={user.name} />
        )}

        {activeTab === "safety" && (
          <SafetyScore
            safetyLevel={user.safetyLevel}
            safetyGrade={user.safetyGrade}
            lastInspection={user.lastInspection}
            nextInspection={user.nextInspection}
            insurance={user.insurance}
            devices={user.devices}
          />
        )}

        {activeTab === "ai" && (
          <AIAnalysisPanel />
        )}

        {activeTab === "portrait" && hasPortrait && (
          <UserPortrait
            portrait={(user as { portrait: Parameters<typeof UserPortrait>[0]["portrait"] }).portrait}
            userName={user.name}
            yearlyUsage={user.yearlyUsage}
          />
        )}

        {/* Purchase history quick view */}
        {activeTab === "overview" && (
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-800">最近购气记录</h3>
              <button className="text-xs text-[#E8531A] cursor-pointer">查看全部</button>
            </div>
            <div className="space-y-2">
              {[
                { date: "2026-04-10 09:15", amount: 200, method: "微信支付", status: "成功", iot: "已下发" },
                { date: "2026-03-05 14:22", amount: 300, method: "支付宝", status: "成功", iot: "已下发" },
                { date: "2026-02-01 10:08", amount: 200, method: "银联", status: "成功", iot: "已下发" },
                { date: "2026-01-08 16:35", amount: 500, method: "微信支付", status: "成功", iot: "已下发" },
              ].map((rec, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-money-cny-circle-line text-[#E8531A] text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">购气 ¥{rec.amount}</p>
                      <p className="text-xs text-gray-400">{rec.date} · {rec.method}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600">{rec.status}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-600">{rec.iot}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
